import { useState, type ReactNode } from 'react';
import type { BoardroomCatalogueEntry, BoardroomDeliverable } from '../../../types';
import { isStoryRoom, viewsFor, type RoomView } from './views';

/** The views by the question each answers, as the mockup groups them. */
export const VIEW_GROUPS: { label: string; views: string[] }[] = [
  { label: 'Who is here', views: ['competitors', 'search'] },
  { label: 'What they sell', views: ['pricing', 'features', 'selling'] },
  { label: 'What people want', views: ['complaints', 'audience', 'ideas'] },
];

/** A story room's views, by the reporter's question each answers. */
export const STORY_VIEW_GROUPS: { label: string; views: string[] }[] = [
  { label: 'The story', views: ['story', 'people'] },
  { label: 'What people say and search', views: ['says', 'audience', 'search'] },
  { label: 'What to write', views: ['coverage', 'angles'] },
];

/** A story room's own line for a view both kinds share. */
export const STORY_VIEW_HINTS: Record<string, string> = {
  search: 'What people search about it, and how much.',
};

/** What each view is for, in a line. */
export const VIEW_HINTS: Record<string, string> = {
  competitors: 'Everyone in this market, what they sell and what it costs.',
  search: 'What buyers search for, how much, and who shows up.',
  pricing: 'What each product costs, and how the market is tiered.',
  features: 'What most products ship, and what only one does.',
  selling: "Each company's pitch, its lead magnets and how to reach it.",
  complaints: 'What customers complain about, and what nobody fixes.',
  audience: 'Where people in this market post and talk, ranked for joining in.',
  ideas: 'Products worth building, each with the complaints and numbers behind it.',
  story: 'What happened and when, who it involved, and why it is news now.',
  people: 'The people in the story and what each said on the record.',
  says: 'What people say about it, for and against, and when.',
  coverage: 'Who already ranks for the searches about it, and how well they answer.',
  angles: 'What people search about it that nobody answers well: posts to write.',
};

/** Whether a view is on the dashboard: any of its own tables is pinned. Pure. */
export function viewOnDashboard(view: RoomView, deliverables: BoardroomDeliverable[]): boolean {
  return deliverables.some(
    d => d.pinned && d.status !== 'dropped' && !d.subject && view.tabs.some(t => t.kind === d.kind),
  );
}

/** The table that adding a view pins: its first tab this room can show. Pure. */
export function kindToAdd(view: RoomView, catalogue: BoardroomCatalogueEntry[]): string | null {
  return view.tabs.find(t => catalogue.some(c => c.kind === t.kind))?.kind ?? null;
}

/**
 * ADD A VIEW, IN ONE STEP. Adding a card meant learning pin, unpin, save,
 * "Not saved", built-in and "added by you". A view is on the dashboard or it
 * is not: here are the seven, by the question each answers, and a box to ask
 * the chat for one that is not here. Every table and card the room has is
 * still under them, folded away.
 */
export default function ViewPicker({
  deliverables,
  catalogue,
  onAdd,
  onOpen,
  onDescribe,
  everything,
  roomKind,
}: {
  /** What kind of question the room asks: a story room offers a reporter's views. */
  roomKind?: string | null;
  deliverables: BoardroomDeliverable[];
  catalogue: BoardroomCatalogueEntry[];
  /** Put the view on the dashboard by pinning this table. */
  onAdd: (kind: string) => void;
  /** Open a view that is already on the dashboard. */
  onOpen: (kind: string) => void;
  /** Ask the chat for a view described in words. */
  onDescribe: (text: string) => void;
  /** Every table and card the room has, to pin, unpin or delete one by one. */
  everything?: ReactNode;
}) {
  const [text, setText] = useState('');
  return (
    <div className="flex flex-col gap-5" data-testid="view-picker">
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Pick the question you want answered. The room fills it in on the next dig.
      </p>
      {(isStoryRoom(roomKind) ? STORY_VIEW_GROUPS : VIEW_GROUPS).map(g => (
        <section key={g.label} className="flex flex-col gap-2">
          <h3 className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">{g.label}</h3>
          {g.views.map(key => {
            const view = viewsFor(roomKind).find(v => v.key === key);
            if (!view) return null;
            const kind = kindToAdd(view, catalogue);
            if (!kind) return null;
            const on = viewOnDashboard(view, deliverables);
            return (
              <div
                key={key}
                className="flex items-center gap-3 rounded-lg border border-gray-200 bg-white px-3 py-2.5 dark:border-gray-700 dark:bg-gray-800"
                data-testid={`view-pick-${key}`}
              >
                <div className="min-w-0 flex-1">
                  <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{view.label}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400">{(isStoryRoom(roomKind) && STORY_VIEW_HINTS[key]) || VIEW_HINTS[key]}</div>
                </div>
                {on ? (
                  <button
                    type="button"
                    onClick={() => onOpen(kind)}
                    className="shrink-0 text-xs text-emerald-700 hover:underline dark:text-emerald-300"
                  >
                    On your dashboard
                  </button>
                ) : (
                  <button
                    type="button"
                    onClick={() => onAdd(kind)}
                    className="shrink-0 rounded bg-indigo-600 px-3 py-1 text-xs font-medium text-white hover:bg-indigo-500"
                    data-testid={`view-add-${key}`}
                  >
                    Add
                  </button>
                )}
              </div>
            );
          })}
        </section>
      ))}
      <form
        className="flex flex-col gap-1.5 border-t border-gray-200 pt-4 dark:border-gray-700"
        onSubmit={e => {
          e.preventDefault();
          if (text.trim()) onDescribe(text.trim());
        }}
      >
        <label className="text-sm font-medium text-gray-900 dark:text-gray-100" htmlFor="view-describe">
          Or describe the view you want
        </label>
        <div className="flex gap-2">
          <input
            id="view-describe"
            value={text}
            onChange={e => setText(e.target.value)}
            placeholder="e.g. compare the EHRs each company connects to, by practice size"
            className="min-w-0 flex-1 rounded border border-gray-300 bg-white px-2 py-1.5 text-sm text-gray-900 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-100"
          />
          <button
            type="submit"
            disabled={!text.trim()}
            className="shrink-0 rounded border border-gray-300 px-3 py-1 text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
          >
            Ask the chat
          </button>
        </div>
        <span className="text-xs text-gray-500 dark:text-gray-400">
          The chat builds it from what the room has found.
        </span>
      </form>
      {everything && (
        <details className="border-t border-gray-200 pt-3 dark:border-gray-700">
          <summary className="cursor-pointer text-xs text-gray-500 dark:text-gray-400">
            Every table and card in this room
          </summary>
          <div className="mt-2">{everything}</div>
        </details>
      )}
    </div>
  );
}
